<template>
  <div ref="geetestContainer" class="geetest-wrapper">
    <div class="geetest-container"></div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { geetestRegisterAPI, type GeetestValidate } from "@/api/auth/auth";

const props = defineProps<{
  product?: string;
  autoShow?: boolean;
}>();

const emit = defineEmits<{
  (e: "success", data: GeetestValidate): void;
}>();

const geetestContainer = ref<HTMLElement | null>(null);
let captchaObj: any = null;
let geetestScriptPromise: Promise<void> | null = null;

const waitForGeetestReady = () => {
  return new Promise<void>((resolve, reject) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.initGeetest4) {
        window.clearInterval(timer);
        resolve();
        return;
      }

      if (Date.now() - startedAt > 10000) {
        window.clearInterval(timer);
        reject(new Error("Geetest script load timeout"));
      }
    }, 50);
  });
};

const ensureGeetestScript = () => {
  if (window.initGeetest4) {
    return Promise.resolve();
  }

  if (geetestScriptPromise) {
    return geetestScriptPromise;
  }

  const scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://static.geetest.com/v4/gt4.js"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => waitForGeetestReady().then(resolve).catch(reject), {
        once: true,
      });
      existingScript.addEventListener("error", () => reject(new Error("Geetest script load failed")), {
        once: true,
      });
      waitForGeetestReady().then(resolve).catch(reject);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://static.geetest.com/v4/gt4.js";
    script.async = true;
    script.onload = () => waitForGeetestReady().then(resolve).catch(reject);
    script.onerror = () => reject(new Error("Geetest script load failed"));
    document.head.appendChild(script);
  });

  geetestScriptPromise = scriptLoadPromise.catch((error) => {
    geetestScriptPromise = null;
    throw error;
  });

  return geetestScriptPromise;
};

onMounted(async () => {
  try {
    await ensureGeetestScript();

    const response = await geetestRegisterAPI();
    const registerData = response.data;

    window.initGeetest4(
      {
        captchaId: registerData.captchaId,
        product: props.product || "popup",
        language: "zho",
        protocol: "https://",
        outside: false,
        autoShow: props.autoShow || false,
      },
      (captcha: any) => {
        captchaObj = captcha;
        captcha.appendTo(geetestContainer.value).onSuccess(() => {
          const result = captcha.getValidate();
          emit("success", result);
        });

        if (props.autoShow) {
          captcha.showCaptcha();
        }
      }
    );
  } catch (error) {
    console.error("Failed to initialize Geetest:", error);
  }
});

const reset = () => {
  if (captchaObj) {
    captchaObj.reset();
  }
};

const showCaptcha = () => {
  if (!captchaObj) {
    return false;
  }

  captchaObj.showCaptcha();
  return true;
};

defineExpose({ reset, showCaptcha });
</script>

<style scoped>
.geetest-wrapper,
.geetest-container {
  width: 100% !important;
}

:deep(.geetest_holder),
:deep(.geetest_form),
:deep(.geetest_btn_click) {
  width: 100% !important;
}
</style>
